import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.scss';

// Contexts
import { AuthProvider, useAuth } from './core/services/auth/AuthContext';
import { ProjectsProvider } from './core/contexts/ProjectsContext';

// Error boundary for catching runtime errors
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean, errorInfo: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorInfo: '' };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Application error:', error, errorInfo);
    this.setState({
      errorInfo: errorInfo.componentStack || 'Unknown error stack'
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h2>Something went wrong.</h2>
          <button onClick={() => window.location.reload()}>Refresh the page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Loading component for suspense fallback
const LoadingFallback: React.FC = () => (
  <div className="loading-fallback">
    <div className="loading-content">
      <div className="loading-spinner"></div>
      <div className="loading-text">
        <p>Loading...</p>
      </div>
    </div>
  </div>
);

// Lazy load components for better code splitting
const SignIn = lazy(() => import('./features/signin/SignIn'));
const SignUp = lazy(() => import('./features/signup/SignUp'));
const ForgotPassword = lazy(() => import('./features/forgot-password/ForgotPassword'));
const MetaCallback = lazy(() => import('./features/meta-callback/MetaCallback'));
const Dashboard = lazy(() => import('./features/dashboard/Dashboard'));
const PageNotFound = lazy(() => import('./features/page-not-found/PageNotFound'));
const MainLayout = lazy(() => import('./features/shared/MainLayout'));

// Modules
const InsightsPage = lazy(() => import('./modules/insights/pages/InsightsPage'));
const InsightsModule = lazy(() => import('./modules/insights/InsightsModule'));

// Protected route component for auth logic
interface ProtectedRouteProps {
  element: React.ReactElement;
  redirectPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  element,
  redirectPath = '/signin'
}) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If not authenticated, redirect to sign in
    if (!isAuthenticated) {
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectPath]);

  // If not authenticated, render nothing while redirecting
  if (!isAuthenticated) {
    return <div className="loading-state">Redirecting...</div>;
  }

  return element;
};

// Public route component that redirects authenticated users
interface PublicRouteProps {
  element: React.ReactElement;
  redirectPath?: string;
}

const PublicRoute: React.FC<PublicRouteProps> = ({
  element,
  redirectPath = '/dashboard'
}) => {
  const { isFullyAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If fully authenticated, redirect to dashboard
    if (isFullyAuthenticated) {
      navigate(redirectPath, { replace: true });
    }
  }, [isFullyAuthenticated, navigate, redirectPath]);

  // If fully authenticated, render nothing while redirecting
  if (isFullyAuthenticated) {
    return <div className="loading-state">Redirecting...</div>;
  }

  return element;
};

// App routes component
const AppRoutes: React.FC = () => {
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/signin" element={<PublicRoute element={<SignIn />} />} />
          <Route path="/signup" element={<PublicRoute element={<SignUp />} />} />
          <Route path="/forgot-password" element={<PublicRoute element={<ForgotPassword />} />} />
          <Route path="/meta-callback" element={<MetaCallback />} />

          {/* Protected routes with shared layout */}
          <Route path="/" element={<ProtectedRoute element={<MainLayout />} />}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="insights/*" element={<InsightsModule />} />
            <Route path="campaigns/*" element={<div>Campaigns Module (Coming Soon)</div>} />
            <Route path="analytics/*" element={<div>Analytics Module (Coming Soon)</div>} />
            <Route index element={<Navigate to="/dashboard" replace />} />
          </Route>
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App: React.FC = () => {
  return (
    <div className="app-container">
      <ErrorBoundary>
        <AuthProvider>
          <ProjectsProvider>
            <AppRoutes />
          </ProjectsProvider>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
};

export default App;